import {
  AUTH_EVENTS,
  AUTO_ENROLLMENT_EVENTS,
  SOCIAL_EVENTS,
} from "@/constants/analyticsEvents";
import {
  Outlet,
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import {
  convexQuery,
  useConvexAuth,
  useConvexMutation,
} from "@convex-dev/react-query";
import { useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Route as ApplyPartnershipRoute } from "@/routes/_app/_auth/dashboard/_layout.partnerships.apply";
import { ConvexError } from "convex/values";
import { Route as DashboardRoute } from "@/routes/_app/_auth/dashboard/_layout";
import { Route as LeaderboardRoute } from "@/routes/_app/_auth/dashboard/_layout.customLeaderboard";
import { Route as OnboardingNameRoute } from "@/routes/_app/_auth/onboarding/_layout.name";
import { User } from "~/types";
import { api } from "@cvx/_generated/api";
import { useAccount } from "@/state/useAccount";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useToast } from "@/components/ui/use-toast";

export const Route = createFileRoute("/_app/_auth")({
  component: AuthLayout,
});

// No need for complex OAuth parameter handling anymore since we use a public route

// Helper function to determine if creator should be redirected to leaderboard
function shouldRedirectToLeaderboard(user: User | undefined) {
  return (
    user?.creator &&
    user.creatorPartnerships.length > 0 &&
    user.creator.showLeaderboardOnHomeScreen === true
  );
}

function AuthLayout() {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));
  const {
    setContractId,
    setAppId,
    setCreatorId,
    appId,
    creatorId,
    contractId,
    pendingCampaignId,
    clearPendingCampaignId,
    demoModeEnabled,
  } = useAccount();
  const { identify, track, setUserProperties } = useMixpanel();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const enrollmentAttemptedRef = useRef(false);

  console.log("VITE CONVEX URL", import.meta.env.VITE_CONVEX_URL);

  // Fetch pending campaign if it exists
  const { data: pendingCampaign } = useQuery({
    ...convexQuery(api.campaigns.getPublicCampaignInfo, {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      campaignId: pendingCampaignId!,
    }),
    enabled: !!pendingCampaignId && !!user,
  });

  // Mutation to join campaign
  const { mutateAsync: joinCampaign } = useMutation({
    mutationFn: useConvexMutation(
      api.partnerships.joinCampaignWithoutApplication,
    ),
  });

  // Reset enrollment attempt flag when pendingCampaignId changes
  useEffect(() => {
    if (!pendingCampaignId) {
      enrollmentAttemptedRef.current = false;
    }
  }, [pendingCampaignId]);

  // Handle account type setting first
  useEffect(() => {
    // Don't do anything if not authenticated - bail out immediately
    if (!isAuthenticated) {
      if (!isLoading) {
        navigate({ to: "/login" });
      }
      return;
    }

    // If loading or no user data yet, wait
    if (isLoading || !user) return;

    // Identify user for analytics
    identify(user.email ?? user._id);

    // Track daily login
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
    const lastLoginDate = localStorage.getItem("lastLoginDate");

    if (lastLoginDate !== today) {
      // Track login event
      track(AUTH_EVENTS.USER_DAILY_LOGIN, {
        user_id: user._id,
        email: user.email,
        login_date: today,
        user_type: user.creator
          ? "creator"
          : user.appsList.length > 0
            ? "founder"
            : "unknown",
        has_social_accounts: !!(
          user.creator &&
          user.creatorSocials &&
          ((user.creatorSocials.instagram &&
            user.creatorSocials.instagram.length > 0) ||
            (user.creatorSocials.youtube &&
              user.creatorSocials.youtube.length > 0) ||
            (user.creatorSocials.tiktok &&
              user.creatorSocials.tiktok.length > 0))
        ),
        connected_platforms:
          user.creator && user.creatorSocials
            ? [
                ...(user.creatorSocials.instagram &&
                user.creatorSocials.instagram.length > 0
                  ? ["instagram"]
                  : []),
                ...(user.creatorSocials.youtube &&
                user.creatorSocials.youtube.length > 0
                  ? ["youtube"]
                  : []),
                ...(user.creatorSocials.tiktok &&
                user.creatorSocials.tiktok.length > 0
                  ? ["tiktok"]
                  : []),
              ]
            : [],
        total_connected_accounts:
          user.creator && user.creatorSocials
            ? (user.creatorSocials.instagram?.length || 0) +
              (user.creatorSocials.youtube?.length || 0) +
              (user.creatorSocials.tiktok?.length || 0)
            : 0,
        partnerships_count: user.creatorPartnerships?.length || 0,
        apps_count: user.appsList?.length || 0,
      });

      // Update user properties with latest info
      setUserProperties({
        user_type: user.creator
          ? "creator"
          : user.appsList.length > 0
            ? "founder"
            : "unknown",
        email: user.email,
        name: user.name,
        has_social_accounts: !!(
          user.creator &&
          user.creatorSocials &&
          ((user.creatorSocials.instagram &&
            user.creatorSocials.instagram.length > 0) ||
            (user.creatorSocials.youtube &&
              user.creatorSocials.youtube.length > 0) ||
            (user.creatorSocials.tiktok &&
              user.creatorSocials.tiktok.length > 0))
        ),
        connected_platforms:
          user.creator && user.creatorSocials
            ? [
                ...(user.creatorSocials.instagram &&
                user.creatorSocials.instagram.length > 0
                  ? ["instagram"]
                  : []),
                ...(user.creatorSocials.youtube &&
                user.creatorSocials.youtube.length > 0
                  ? ["youtube"]
                  : []),
                ...(user.creatorSocials.tiktok &&
                user.creatorSocials.tiktok.length > 0
                  ? ["tiktok"]
                  : []),
              ]
            : [],
        total_connected_accounts:
          user.creator && user.creatorSocials
            ? (user.creatorSocials.instagram?.length || 0) +
              (user.creatorSocials.youtube?.length || 0) +
              (user.creatorSocials.tiktok?.length || 0)
            : 0,
        partnerships_count: user.creatorPartnerships?.length || 0,
        apps_count: user.appsList?.length || 0,
        last_login_date: today,
      });

      // Store the login date
      localStorage.setItem("lastLoginDate", today);
    }

    if (!user.name || !user.placeOfResidence || !user.howDidYouHearAboutUs) {
      navigate({ to: OnboardingNameRoute.fullPath });
      return;
    }

    // Handle pending campaign enrollment after onboarding is complete
    if (
      pendingCampaignId &&
      pendingCampaign &&
      !enrollmentAttemptedRef.current
    ) {
      enrollmentAttemptedRef.current = true;

      (async () => {
        try {
          // Only auto-enroll for challenge campaigns (applicationNotRequired)
          if (pendingCampaign.applicationNotRequired) {
            const newContractId = await joinCampaign({
              campaignId: pendingCampaignId,
            });

            // Track successful enrollment
            track(AUTO_ENROLLMENT_EVENTS.CAMPAIGN_AUTO_ENROLLED_AFTER_LOGIN, {
              user_id: user._id,
              campaign_id: pendingCampaign._id,
              campaign_name: pendingCampaign.name,
              app_id: pendingCampaign.appId,
              app_name: pendingCampaign.app.name,
              contract_id: newContractId,
              timestamp: new Date().toISOString(),
            });

            // Store flag for specific campaign to hide opportunities button
            if (pendingCampaignId === "nd718gyfmc7m4jkv93px8m29qn7s05m7") {
              localStorage.setItem(
                "hideOpportunitiesForCampaign",
                newContractId,
              );
            }

            toast({
              title: "Successfully enrolled in campaign!",
              description: `You've been enrolled in ${pendingCampaign.app.name}'s campaign.`,
            });

            // Set the creator and contract IDs
            setCreatorId(user.creator?._id);
            setContractId(newContractId);
            clearPendingCampaignId();

            // Navigate to dashboard
            navigate({ to: DashboardRoute.fullPath });
            return;
          } else {
            // Campaign requires application, redirect to apply page
            clearPendingCampaignId();
            navigate({
              to: ApplyPartnershipRoute.fullPath,
              search: { selectedCampaignId: pendingCampaignId },
            });
            return;
          }
        } catch (error) {
          console.error("Failed to auto-enroll in campaign:", error);

          // Show toast error but don't interrupt the flow
          toast({
            title: "Failed to enroll in campaign",
            description:
              error instanceof ConvexError
                ? error.data
                : "Please try enrolling again from the campaigns page.",
            variant: "destructive",
          });

          // Clear the pending campaign ID
          clearPendingCampaignId();

          // Continue with normal flow - don't return here so the rest of the logic runs
        }
      })();

      // Return to prevent the rest of the logic from running while enrollment is in progress
      return;
    }

    // handle edge case where appId is not properly wiped from state after user logs out
    // TODO: investigate why this sometimes happens.
    if (appId && !user.appsList.some((app) => app._id === appId)) {
      setAppId(undefined);
      if (user.appsList.length > 0) {
        setAppId(user.appsList[0]._id);
        navigate({ to: DashboardRoute.fullPath });
        return;
      } else if (user.creatorPartnerships.length > 0) {
        setCreatorId(user.creatorPartnerships[0].creatorId);
        setContractId(user.creatorPartnerships[0]._id);
        navigate({
          to: shouldRedirectToLeaderboard(user)
            ? LeaderboardRoute.fullPath
            : DashboardRoute.fullPath,
        });
        return;
      } else {
        setCreatorId(user.creator?._id);
        navigate({ to: ApplyPartnershipRoute.fullPath });
        return;
      }
    }

    // handle edge case where creatorId is not properly wiped from state after user logs out
    // TODO: investigate why this sometimes happens.
    if (creatorId && user.creator?._id !== creatorId) {
      setCreatorId(undefined);
      if (user.creatorPartnerships.length > 0) {
        setCreatorId(user.creator?._id);
        navigate({
          to: shouldRedirectToLeaderboard(user)
            ? LeaderboardRoute.fullPath
            : DashboardRoute.fullPath,
        });
        return;
      } else if (user.appsList.length > 0) {
        setAppId(user.appsList[0]._id);
        navigate({ to: DashboardRoute.fullPath });
        return;
      } else {
        setCreatorId(user.creator?._id);
        navigate({ to: ApplyPartnershipRoute.fullPath });
        return;
      }
    }

    // handle edge case where contractId is not properly wiped from state after user logs out
    if (
      contractId &&
      !user.creatorPartnerships.some(
        (partnership) => partnership._id === contractId,
      )
    ) {
      setContractId(undefined);
      if (user.creatorPartnerships.length > 0) {
        setCreatorId(user.creatorPartnerships[0].creatorId);
        navigate({
          to: shouldRedirectToLeaderboard(user)
            ? LeaderboardRoute.fullPath
            : DashboardRoute.fullPath,
        });
        return;
      } else if (user.appsList.length > 0) {
        setAppId(user.appsList[0]._id);
        navigate({ to: DashboardRoute.fullPath });
        return;
      } else {
        setCreatorId(user.creator?._id);
        navigate({ to: ApplyPartnershipRoute.fullPath });
        return;
      }
    }

    // user is logging in for the first time
    if (!appId && !creatorId && !contractId) {
      if (user.appsList.length > 0) {
        setAppId(user.appsList[0]._id);
        navigate({ to: DashboardRoute.fullPath });
        return;
      } else if (user.creatorPartnerships.length > 0) {
        setCreatorId(user.creatorPartnerships[0].creatorId);
        navigate({
          to: shouldRedirectToLeaderboard(user)
            ? LeaderboardRoute.fullPath
            : DashboardRoute.fullPath,
        });
        return;
      } else {
        setCreatorId(user.creator?._id);
        navigate({ to: ApplyPartnershipRoute.fullPath });
        return;
      }
    } // otherwise, creatorid/appid/contractId will be based on user toggling on the frontend
  }, [
    user,
    setContractId,
    setAppId,
    isLoading,
    isAuthenticated,
    setCreatorId,
    pendingCampaignId,
    pendingCampaign,
    clearPendingCampaignId,
    demoModeEnabled,
    appId,
    location.pathname,
    navigate,
    toast,
    joinCampaign,
    track,
  ]);

  // Track social account connections when user data changes
  useEffect(() => {
    if (!user?.creator || !user.creatorSocials) return;

    // Get current connected platforms
    const currentConnectedPlatforms = [
      ...(user.creatorSocials.instagram &&
      user.creatorSocials.instagram.length > 0
        ? ["instagram"]
        : []),
      ...(user.creatorSocials.youtube && user.creatorSocials.youtube.length > 0
        ? ["youtube"]
        : []),
      ...(user.creatorSocials.tiktok && user.creatorSocials.tiktok.length > 0
        ? ["tiktok"]
        : []),
    ];

    const currentTotalAccounts =
      (user.creatorSocials.instagram?.length || 0) +
      (user.creatorSocials.youtube?.length || 0) +
      (user.creatorSocials.tiktok?.length || 0);

    // Check if this is a successful OAuth redirect (has relevant parameters in URL)
    const urlParams = new URLSearchParams(window.location.search);
    const hasOAuthParams = urlParams.has("code") || urlParams.has("state");

    // Get stored previous counts
    const storedPlatforms = localStorage.getItem("connectedPlatforms");
    const storedTotalAccounts = localStorage.getItem("totalConnectedAccounts");

    const previousPlatforms = storedPlatforms
      ? JSON.parse(storedPlatforms)
      : [];
    const previousTotalAccounts = storedTotalAccounts
      ? parseInt(storedTotalAccounts)
      : 0;

    // Check if we have new connections
    const newPlatforms = currentConnectedPlatforms.filter(
      (platform) => !previousPlatforms.includes(platform),
    );
    const accountsIncrease = currentTotalAccounts > previousTotalAccounts;

    if (newPlatforms.length > 0 || (accountsIncrease && hasOAuthParams)) {
      // Track successful connection
      newPlatforms.forEach((platform) => {
        track(SOCIAL_EVENTS.CONNECTION_SUCCESSFUL, {
          platform: platform,
          user_id: user._id,
          creator_id: user.creator?._id,
          timestamp: new Date().toISOString(),
          total_connected_platforms_after: currentConnectedPlatforms.length,
          total_connected_accounts_after: currentTotalAccounts,
          newly_connected_accounts:
            currentTotalAccounts - previousTotalAccounts,
          is_first_social_connection: previousTotalAccounts === 0,
        });
      });

      // If accounts increased but no new platforms (multiple accounts on same platform)
      if (accountsIncrease && newPlatforms.length === 0) {
        track(SOCIAL_EVENTS.ADDITIONAL_ACCOUNT_CONNECTED, {
          user_id: user._id,
          creator_id: user.creator._id,
          timestamp: new Date().toISOString(),
          total_connected_accounts_after: currentTotalAccounts,
          newly_connected_accounts:
            currentTotalAccounts - previousTotalAccounts,
          connected_platforms: currentConnectedPlatforms,
        });
      }
    }

    // Update stored values
    localStorage.setItem(
      "connectedPlatforms",
      JSON.stringify(currentConnectedPlatforms),
    );
    localStorage.setItem(
      "totalConnectedAccounts",
      currentTotalAccounts.toString(),
    );
  }, [user?.creatorSocials, track, user?._id, user?.creator]);

  if (isLoading && !isAuthenticated) {
    return null;
  }

  return <Outlet />;
}
